import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Spinner, Table } from 'reactstrap';

const monthKey = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
};

const formatHours = (hours) => (hours == null ? '—' : Number(hours).toFixed(2));
const formatMinutes = (mins) => (mins == null ? '—' : Number(mins).toFixed(1));
const formatWhen = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const formatLocation = (loc) => {
    if (!loc || loc.latitude == null || loc.longitude == null) return '—';
    return `${Number(loc.latitude).toFixed(4)}, ${Number(loc.longitude).toFixed(4)}`;
};

const emptyFilters = {
    serial: '',
    deviceName: '',
    lastCustomer: '',
    customers: '',
    location: ''
};

const compareValues = (a, b, key) => {
    const av = a[key];
    const bv = b[key];

    if (key === 'lastRunAt' || key === 'uploadedAt' || key === 'startedAt' || key === 'endedAt') {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        return at - bt;
    }
    if (key === 'runCount' || key === 'totalHours' || key === 'totalDurationMinutes' || key === 'durationMinutes') {
        return (Number(av) || 0) - (Number(bv) || 0);
    }
    if (key === 'lastLocation') {
        return formatLocation(av).localeCompare(formatLocation(bv), undefined, { sensitivity: 'base' });
    }
    if (key === 'customers') {
        const as = Array.isArray(av) ? av.join(', ') : (av || '');
        const bs = Array.isArray(bv) ? bv.join(', ') : (bv || '');
        return as.localeCompare(bs, undefined, { sensitivity: 'base', numeric: true });
    }
    if (key === 'location') {
        const as = a.latitude != null && a.longitude != null
            ? `${a.latitude},${a.longitude}` : '';
        const bs = b.latitude != null && b.longitude != null
            ? `${b.latitude},${b.longitude}` : '';
        return as.localeCompare(bs, undefined, { sensitivity: 'base' });
    }

    return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { sensitivity: 'base', numeric: true });
};

const SortableTh = ({ label, sortKey, sort, onSort, style }) => {
    const active = sort.key === sortKey;
    const marker = !active ? '' : sort.dir === 'asc' ? ' ▲' : ' ▼';
    return (
        <th
            style={{ ...style, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
            onClick={() => onSort(sortKey)}
            title={`Sort by ${label}`}
        >
            {label}{marker}
        </th>
    );
};

export const AdminToolRuns = () => {
    const navigate = useNavigate();
    const [index, setIndex] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rebuilding, setRebuilding] = useState(false);
    const [error, setError] = useState('');
    const [month, setMonth] = useState('');
    const [expanded, setExpanded] = useState({});
    const [filters, setFilters] = useState(emptyFilters);
    const [sort, setSort] = useState({ key: 'totalHours', dir: 'desc' });
    const [runSort, setRunSort] = useState({ key: 'uploadedAt', dir: 'desc' });

    useEffect(() => {
        checkAccessAndLoad();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only

    const checkAccessAndLoad = async () => {
        try {
            const response = await fetch('api/identity/getuser');
            const data = await response.json();
            if (!data.roles || !data.roles.some(r => r.toLowerCase() === 'superadmin')) {
                navigate('/');
                return;
            }
            await loadIndex();
        } catch (err) {
            navigate('/');
        }
    };

    const loadIndex = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('api/analytics/tool-runs');
            if (response.status === 403) {
                navigate('/');
                return;
            }
            if (!response.ok) {
                setError('Failed to load tool-runs index.');
                setIndex(null);
            } else {
                setIndex(await response.json());
            }
        } catch (err) {
            setError('Failed to load tool-runs index.');
            setIndex(null);
        }
        setLoading(false);
    };

    const handleRebuild = async () => {
        if (!window.confirm('Rebuild the fleet tool-runs index from all surveys? This may take a while.')) {
            return;
        }
        setRebuilding(true);
        setError('');
        try {
            const response = await fetch('api/analytics/tool-runs/rebuild', { method: 'POST' });
            if (response.status === 403) {
                navigate('/');
                return;
            }
            if (!response.ok) {
                setError('Rebuild failed.');
            } else {
                setIndex(await response.json());
            }
        } catch (err) {
            setError('Rebuild failed.');
        }
        setRebuilding(false);
    };

    const monthScopedTools = useMemo(() => {
        if (!index?.tools) return [];
        if (!month) return index.tools;

        return index.tools
            .map(tool => {
                const runs = (tool.runs || []).filter(run => {
                    const key = monthKey(run.uploadedAt) || monthKey(run.startedAt) || monthKey(run.endedAt);
                    return key === month;
                });
                if (runs.length === 0) return null;
                const totalDurationMinutes = runs.reduce((sum, r) => sum + (Number(r.durationMinutes) || 0), 0);
                const last = [...runs].sort((a, b) => {
                    const at = new Date(a.endedAt || a.uploadedAt || 0).getTime();
                    const bt = new Date(b.endedAt || b.uploadedAt || 0).getTime();
                    return bt - at;
                })[0];
                return {
                    ...tool,
                    runs,
                    runCount: runs.length,
                    totalDurationMinutes,
                    totalHours: totalDurationMinutes / 60,
                    lastRunAt: last?.endedAt || last?.uploadedAt || null,
                    lastCustomer: last?.customer || null,
                    lastLocation: last?.latitude != null && last?.longitude != null
                        ? { latitude: last.latitude, longitude: last.longitude, source: last.locationSource }
                        : null,
                    customers: [...new Set(runs.map(r => r.customer).filter(Boolean))]
                };
            })
            .filter(Boolean);
    }, [index, month]);

    const displayedTools = useMemo(() => {
        const serialQ = filters.serial.trim().toLowerCase();
        const deviceQ = filters.deviceName.trim().toLowerCase();
        const lastCustomerQ = filters.lastCustomer.trim().toLowerCase();
        const customersQ = filters.customers.trim().toLowerCase();
        const locationQ = filters.location.trim().toLowerCase();

        let rows = monthScopedTools.filter(tool => {
            if (serialQ && !(tool.serial || '').toLowerCase().includes(serialQ)) return false;
            if (deviceQ && !(tool.deviceName || '').toLowerCase().includes(deviceQ)) return false;
            if (lastCustomerQ && !(tool.lastCustomer || '').toLowerCase().includes(lastCustomerQ)) return false;
            if (customersQ && !(tool.customers || []).join(', ').toLowerCase().includes(customersQ)) return false;
            if (locationQ && !formatLocation(tool.lastLocation).toLowerCase().includes(locationQ)) return false;
            return true;
        });

        const dir = sort.dir === 'asc' ? 1 : -1;
        rows = [...rows].sort((a, b) => dir * compareValues(a, b, sort.key));
        return rows;
    }, [monthScopedTools, filters, sort]);

    const sortedRunsFor = (runs) => {
        const dir = runSort.dir === 'asc' ? 1 : -1;
        return [...(runs || [])].sort((a, b) => dir * compareValues(a, b, runSort.key));
    };

    const toggleSort = (key) => {
        setSort(prev => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: key === 'serial' || key === 'deviceName' || key === 'lastCustomer' || key === 'customers' ? 'asc' : 'desc' });
    };

    const toggleRunSort = (key) => {
        setRunSort(prev => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: key === 'fileName' || key === 'customer' ? 'asc' : 'desc' });
    };

    const setFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const clearFilters = () => {
        setFilters(emptyFilters);
        setMonth('');
    };

    const hasActiveFilters = month || Object.values(filters).some(v => v.trim());

    const toggleExpanded = (serial) => {
        setExpanded(prev => ({ ...prev, [serial]: !prev[serial] }));
    };

    if (loading) {
        return (
            <div className="container mt-4">
                <Spinner /> Loading tool runs…
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4 px-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h2 className="mb-0">Tool runs</h2>
                    <div className="text-muted small">
                        Fleet utilisation by serial (SuperAdmin).
                        {index?.updated && <> Updated {formatWhen(index.updated)} ({index.builtFrom}).</>}
                        {' '}Showing {displayedTools.length}
                        {monthScopedTools.length !== displayedTools.length ? ` of ${monthScopedTools.length}` : ''}.
                    </div>
                </div>
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <Input
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                        style={{ width: '11rem' }}
                        title="Filter by month (client-side)"
                    />
                    {hasActiveFilters && (
                        <Button color="link" size="sm" onClick={clearFilters}>Clear filters</Button>
                    )}
                    <Button color="secondary" onClick={loadIndex} disabled={rebuilding}>Refresh</Button>
                    <Button color="primary" onClick={handleRebuild} disabled={rebuilding}>
                        {rebuilding ? 'Rebuilding…' : 'Rebuild index'}
                    </Button>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {!monthScopedTools.length ? (
                <p className="text-muted">
                    No tool runs{month ? ` for ${month}` : ''}. Use Rebuild index to compile from existing surveys.
                </p>
            ) : (
                <Table responsive hover size="sm" className="align-middle">
                    <thead>
                        <tr>
                            <th style={{ width: '2rem' }} />
                            <SortableTh label="Serial" sortKey="serial" sort={sort} onSort={toggleSort} />
                            <SortableTh label="Device" sortKey="deviceName" sort={sort} onSort={toggleSort} />
                            <SortableTh label="Runs" sortKey="runCount" sort={sort} onSort={toggleSort} />
                            <SortableTh label="Hours" sortKey="totalHours" sort={sort} onSort={toggleSort} />
                            <SortableTh label="Last run" sortKey="lastRunAt" sort={sort} onSort={toggleSort} />
                            <SortableTh label="Last customer" sortKey="lastCustomer" sort={sort} onSort={toggleSort} />
                            <SortableTh label="Last location" sortKey="lastLocation" sort={sort} onSort={toggleSort} />
                            <SortableTh label="Customers" sortKey="customers" sort={sort} onSort={toggleSort} />
                        </tr>
                        <tr>
                            <th />
                            <th>
                                <Input bsSize="sm" placeholder="Filter…" value={filters.serial}
                                    onChange={(e) => setFilter('serial', e.target.value)} />
                            </th>
                            <th>
                                <Input bsSize="sm" placeholder="Filter…" value={filters.deviceName}
                                    onChange={(e) => setFilter('deviceName', e.target.value)} />
                            </th>
                            <th />
                            <th />
                            <th />
                            <th>
                                <Input bsSize="sm" placeholder="Filter…" value={filters.lastCustomer}
                                    onChange={(e) => setFilter('lastCustomer', e.target.value)} />
                            </th>
                            <th>
                                <Input bsSize="sm" placeholder="Filter…" value={filters.location}
                                    onChange={(e) => setFilter('location', e.target.value)} />
                            </th>
                            <th>
                                <Input bsSize="sm" placeholder="Filter…" value={filters.customers}
                                    onChange={(e) => setFilter('customers', e.target.value)} />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {!displayedTools.length ? (
                            <tr>
                                <td colSpan={9} className="text-muted">No rows match the current filters.</td>
                            </tr>
                        ) : displayedTools.map(tool => (
                            <React.Fragment key={tool.serial}>
                                <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpanded(tool.serial)}>
                                    <td>{expanded[tool.serial] ? '▾' : '▸'}</td>
                                    <td className="fw-semibold">{tool.serial}</td>
                                    <td>{tool.deviceName || '—'}</td>
                                    <td>{tool.runCount}</td>
                                    <td>{formatHours(tool.totalHours)}</td>
                                    <td>{formatWhen(tool.lastRunAt)}</td>
                                    <td>{tool.lastCustomer || '—'}</td>
                                    <td>{formatLocation(tool.lastLocation)}</td>
                                    <td>{(tool.customers || []).join(', ') || '—'}</td>
                                </tr>
                                {expanded[tool.serial] && (
                                    <tr>
                                        <td colSpan={9} className="bg-light">
                                            <Table responsive size="sm" className="mb-0">
                                                <thead>
                                                    <tr>
                                                        <SortableTh label="File" sortKey="fileName" sort={runSort} onSort={toggleRunSort} />
                                                        <SortableTh label="Customer" sortKey="customer" sort={runSort} onSort={toggleRunSort} />
                                                        <SortableTh label="Uploaded" sortKey="uploadedAt" sort={runSort} onSort={toggleRunSort} />
                                                        <SortableTh label="Started" sortKey="startedAt" sort={runSort} onSort={toggleRunSort} />
                                                        <SortableTh label="Ended" sortKey="endedAt" sort={runSort} onSort={toggleRunSort} />
                                                        <SortableTh label="Minutes" sortKey="durationMinutes" sort={runSort} onSort={toggleRunSort} />
                                                        <SortableTh label="Location" sortKey="location" sort={runSort} onSort={toggleRunSort} />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedRunsFor(tool.runs).map(run => (
                                                        <tr key={run.id || run.surveyKey}>
                                                            <td>{run.fileName || run.id}</td>
                                                            <td>{run.customer}</td>
                                                            <td>{formatWhen(run.uploadedAt)}</td>
                                                            <td>{formatWhen(run.startedAt)}</td>
                                                            <td>{formatWhen(run.endedAt)}</td>
                                                            <td>{formatMinutes(run.durationMinutes)}</td>
                                                            <td>
                                                                {run.latitude != null && run.longitude != null
                                                                    ? `${Number(run.latitude).toFixed(4)}, ${Number(run.longitude).toFixed(4)}`
                                                                    : '—'}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
};
